<template lang="pug" src="./referral.pug"></template>
<style lang="scss" src="./referral.scss" scoped></style>

<script>
import { mapGetters, mapState } from 'vuex'
import copyToClipboard from '@/utils/copy'
import scroll from '@/utils/scroll'

export default {
    data: () => ({
        filters: {
            clientId: null,
            leader: null,
            business: null,
            referralCode: null,
            fromDate: null,
            toDate: null
        },
        sorting: {}
    }),
    computed: {
        ...mapGetters(['type', 'permissions']),
        ...mapState({
            leaders: state => state.data.leaders,
            businesses: state => state.data.businesses,
            appliedFilters: state => state.referrals.filters,
            referrals: state => state.referrals.referrals,
            pagination: state => state.referrals.pagination,
            isTableLoad: state => state.referrals.isLoaded,
            codes: state => state.referrals.codes,
            numberOfClients: (state) => state.data.numberOfClients
        }),
        headers: function() {
            const headers = [
                { text: '№', sortable: false, show: ['leader', 'business'], number: true },
                { text: 'clientId', value: 'uuid' },
                { text: 'balance', value: 'balance', show: ['admin'] },
                { text: 'paymentType', value: '"paymentType"', show: ['admin'] },
                { text: 'referralCode', value: '"referralCode"."code"' },
                { text: 'referralCodeName', value: '"referralCode"."name"' },
                { text: 'businessEmail', value: '"referrer"."email"', show: ['leader', 'admin'] },
                { text: 'leaderEmail', value: '"referrer->referrer"."email"', show: ['admin'] },
                { text: 'createdAt', value: '"createdAt"' }
            ]

            if (!this.canWatchBusiness) {
                const removeIndex = headers.findIndex(it => it.text === 'businessEmail')

                if (removeIndex !== -1) {
                    headers.splice(removeIndex, 1)
                }
            }

            if (!this.canWatchLeaders) {
                const removeIndex = headers.findIndex(it => it.text === 'leaderEmail')

                if (removeIndex !== -1) {
                    headers.splice(removeIndex, 1)
                }
            }

            return headers
                .filter(it => it.show ? this.isShow(it.show) : true)
                .map(it => Object.assign(it, {
                    text: it.number ? it.text : this.$vuetify.lang.t(`$vuetify.pages.Referrals.table.${it.text}`)
                }))
        },
        referralCodes: function() {
            return this.codes.map((it) => ({ code: `${it.code} (${it.name})` }))
        },
        referralCode: function() {
            const [code] = this.appliedFilters.referralCode.code.split(' ')
            return this.codes.find({ code })
        },
        isDisabledCode() {
            return (this.type === 'business') ? false : !this.filters.business
        },
        canWatchLeaders() {
            return this.permissions && this.permissions.includes('watch-leaders')
        },
        canWatchBusiness() {
            return this.type === 'leader' || this.permissions && this.permissions.includes('watch-business')
        },
        canWatchReferralCode() {
            return this.canWatchBusiness || this.type === 'business'
        }
    },
    watch: {
        sorting: {
            handler() {
                this.getReferralsByPage()
            },
            deep: true
        }
    },
    created: async function() {
        const assoc = {
            admin: 'getLeaders',
            leader: 'getBusinesses',
            business: 'referrals/getCodes'
        }

        await this.$store.dispatch(assoc[this.type])

        if (this.permissions && this.permissions.includes('watch-business') && !this.permissions.includes('watch-leaders')) {
            await this.$store.dispatch('getBusinesses')
        }

        if (['leader', 'business'].includes(this.type)) {
            await this.$store.dispatch('getNumberOfReferrals')
        }

        this.filters = Object.assign({}, this.appliedFilters)
        this.getReferralsByPage(this.pagination.page)
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('referrals/SET_PAGINATION', { page: 1, length: null })
        next()
    },
    methods: {
        changeFromDate(date) {
            this.filters.fromDate = date
        },
        changeToDate(date) {
            this.filters.toDate = date
        },
        isShow(types) {
            return types.includes(this.type)
        },
        isDisabledBusiness() {
            if (this.type === 'admin' && this.canWatchLeaders) {
                return !this.filters.leader
            }
        },

        onLeaderSelect(leader) {
            if (leader && !this.permissions || this.canWatchBusiness) {
                this.$store.dispatch('getBusinesses', {
                    leaderId: leader.id
                })
            }
            this.filters.business = null
            this.filters.referralCode = null
        },
        async onBusinessSelect(business) {
            if (business) {
                await this.$store.dispatch('referrals/getCodes', business.id)
            }
            this.filters.referralCode = null
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.getReferralsByPage(page, true)
            }
        },
        submit() {
            this.$store.commit('referrals/SET_FILTERS', Object.assign({}, this.filters))
            this.getReferralsByPage()
        },
        getReferralsByPage(page = 1, isScrollEnable = false, excel = false) {
            this.$store.dispatch('referrals/getReferrals', Object.assign({}, {
                excel,
                page,
                column: this.sorting.sortBy,
                direction: this.sorting.descending ? 'desc' : 'asc',
                ...this.appliedFilters
            }, this.appliedFilters.referralCode && {
                referralCode: this.referralCode
            })).then(_ => {
                if (isScrollEnable) {
                    scroll(this.$refs.table)
                }
            })
        },
        async resetSelectedValues() {
            this.filters = Object.assign({}, {
                clientId: null,
                leader: null,
                business: null,
                referralCode: null,
                fromDate: null,
                toDate: null
            })
            await this.$store.commit('referrals/RESET_REFERRALS')
            this.getReferralsByPage()
        },
        copy(text) {
            if (text) {
                copyToClipboard(text)
            }
        },
        exportToExcel() {
            this.getReferralsByPage(1, false, true)
        }
    }
}
</script>
